import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase, isSupabaseReady } from '../supabase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recoveryMode, setRecoveryMode] = useState(false);

  const fetchProfile = async (userId) => {
    if (!isSupabaseReady) return null;
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (error) {
      console.error('[AuthContext] fetchProfile error:', error.message);
      return null;
    }
    return data;
  };

  useEffect(() => {
    if (!isSupabaseReady) {
      try {
        const saved = localStorage.getItem('crm_session');
        if (saved) setSession(JSON.parse(saved));
      } catch (err) {
        console.warn('[AuthContext] Could not parse local session', err);
      }
      setLoading(false);
      return;
    }

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      setSession(s);
      if (s?.user) {
        const p = await fetchProfile(s.user.id);
        setProfile(p);
      }
      setLoading(false);
    });

    // Listen for auth state changes — detect PASSWORD_RECOVERY event
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, s) => {
        if (event === 'PASSWORD_RECOVERY') {
          // User clicked the recovery link — show the reset form
          setSession(s);
          setRecoveryMode(true);
          setLoading(false);
          return;
        }

        setSession(s);

        if (event === 'SIGNED_IN' && recoveryMode) {
          // Stay in recovery mode until password is set
          return;
        }

        if (s?.user) {
          const p = await fetchProfile(s.user.id);
          setProfile(p);
        } else {
          setProfile(null);
          setRecoveryMode(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email, password) => {
    if (!isSupabaseReady) return { error: null, fallback: true };
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    const p = await fetchProfile(data.user.id);
    if (!p) return { error: 'User profile not found. Contact your administrator.' };
    if (p.status !== 'active') {
      await supabase.auth.signOut();
      return { error: 'Your account is not active. Contact your administrator.' };
    }
    setProfile(p);
    return { error: null, profile: p };
  };

  const logout = async () => {
    if (!isSupabaseReady) {
      localStorage.removeItem('crm_session');
      setSession(null);
      setProfile(null);
      return;
    }
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
    setRecoveryMode(false);
  };

  const exitRecoveryMode = () => {
    setRecoveryMode(false);
    setSession(null);
    setProfile(null);
  };

  const isLoggedIn = isSupabaseReady ? !!session : !!session;

  return (
    <AuthContext.Provider value={{
      session,
      profile,
      loading,
      recoveryMode,
      login,
      logout,
      exitRecoveryMode,
      isLoggedIn,
      isSupabaseReady,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);